/**
 * Idle panel: logo + org name + CTA + QR + rotating tip.
 *
 * Spec: signage-device-app-spec §3.1. The QR points at the org's donate
 * page — the backend supplies the full URL in org-content or config
 * branding; we fall back to giving.at/{slug}.
 */

import {
  applyBranding,
  Branding,
  extractBranding,
  formatCurrency,
} from '../rendering/branding.js';
import { renderQrSvg } from './qr.js';

const DEFAULT_TIPS = [
  'Donate from your phone — scan the QR',
  'Every gift, large or small, makes a difference',
  'Set up a recurring gift from the admin dashboard',
];

export function renderIdle(
  root: HTMLElement,
  config: Record<string, unknown> | null,
  orgContent: Record<string, unknown> | null,
  tipIndex: number
): void {
  const branding = extractBranding(config);
  applyBranding(root, branding);

  root.innerHTML = '';
  root.className = 'ag-app ag-idle';

  if (branding.orgLogoUrl) {
    const img = document.createElement('img');
    img.className = 'ag-idle__logo';
    img.src = branding.orgLogoUrl;
    img.alt = branding.orgName;
    root.appendChild(img);
  }

  const orgName = document.createElement('h1');
  orgName.className = 'ag-idle__org-name';
  orgName.textContent = branding.orgName;
  root.appendChild(orgName);

  const cta = document.createElement('div');
  cta.className = 'ag-idle__cta';
  cta.textContent = 'Scan to donate from your phone';
  root.appendChild(cta);

  const qrUrl = idleQrUrl(config, orgContent);
  if (qrUrl) {
    const card = document.createElement('div');
    card.className = 'ag-idle__qr-card';
    card.innerHTML = renderQrSvg(qrUrl, { size: 240 });
    root.appendChild(card);
  }

  const tip = document.createElement('div');
  tip.className = 'ag-idle__tip';
  tip.textContent = selectTip(config, tipIndex);
  root.appendChild(tip);

  // Bottom status strip: current time + org YTD (if orgContent loaded).
  const bottom = document.createElement('div');
  bottom.className = 'ag-idle__bottom';
  const timeEl = document.createElement('span');
  timeEl.textContent = formatTimeInZone(branding.timezone);
  bottom.appendChild(timeEl);
  const ytd = orgContent?.['aggregate'] as Record<string, unknown> | undefined;
  if (ytd?.['total_raised_ytd_minor_units'] != null) {
    const ytdEl = document.createElement('span');
    const cents =
      typeof ytd['total_raised_ytd_minor_units'] === 'number'
        ? (ytd['total_raised_ytd_minor_units'] as number)
        : 0;
    ytdEl.textContent = `Raised YTD: ${formatCurrency(cents, branding)}`;
    bottom.appendChild(ytdEl);
  }
  root.appendChild(bottom);
}

function idleQrUrl(
  config: Record<string, unknown> | null,
  orgContent: Record<string, unknown> | null
): string | null {
  // Prefer a backend-supplied full URL; fall back to constructing one from slug.
  const contentUrl = orgContent?.['donate_page_url'];
  if (typeof contentUrl === 'string' && contentUrl.trim().length > 0) {
    return contentUrl;
  }
  const branding = config
    ? (config['branding'] as Record<string, unknown> | undefined)
    : undefined;
  const brandingUrl = branding?.['donate_page_url'];
  if (typeof brandingUrl === 'string' && brandingUrl.trim().length > 0) {
    return brandingUrl;
  }
  const slug = branding?.['org_slug'];
  if (typeof slug === 'string' && slug.trim().length > 0) {
    return `https://giving.at/${encodeURIComponent(slug.trim())}`;
  }
  return null;
}

function selectTip(
  config: Record<string, unknown> | null,
  index: number
): string {
  const tipsRaw = config?.['idle_tips'];
  const tips = Array.isArray(tipsRaw)
    ? (tipsRaw.filter((t) => typeof t === 'string') as string[])
    : DEFAULT_TIPS;
  if (tips.length === 0) return DEFAULT_TIPS[0]!;
  const safeIndex = ((index % tips.length) + tips.length) % tips.length;
  return tips[safeIndex]!;
}

function formatTimeInZone(timezone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    }).format(new Date());
  } catch {
    return new Date().toLocaleTimeString();
  }
}

// Exposed for testability.
export const _idleInternals = { selectTip, idleQrUrl };
export type { Branding };
